import { backend } from '@/services/backend'
import { ValidationState } from '@/helpers/form'
import type { NotificationSettings } from './interfaces'
import type { NewEmail, NewPassword } from '@/models/interfaces.auth'

import { getEmail } from '@/services/auth'
import { Sentry } from '@/services/sentry'
import { notificationService } from '@/services/notification'

export class SettingsView {
  private _isLoading: boolean
  private _isError: boolean

  private _notificationSettings?: NotificationSettings

  isDeviceNotificationsEnabled: boolean = false

  constructor() {
    this._isLoading = false
    this._isError = false
  }

  public async init(): Promise<void> {
    try {
      this._isLoading = true
      this._notificationSettings = await backend.getNotificationSettings()
      await this.updateNotificationPermissions()
    } catch (error: any) {
      this._isError = true
      Sentry.captureException(error)
    } finally {
      this._isLoading = false
    }
  }

  async updateNotificationPermissions(): Promise<void> {
    this.isDeviceNotificationsEnabled =
      (await notificationService.checkPermission()) === 'granted'
  }

  public get isLoaded(): boolean {
    return (
      !this._isLoading &&
      !this._isError &&
      this._notificationSettings !== undefined
    )
  }

  public get isLoading(): boolean {
    return this._isLoading
  }

  public get isError(): boolean {
    return this._isError
  }

  public get notificationSettings():
    | Readonly<NotificationSettings>
    | undefined {
    return this._notificationSettings
  }

  public async toggleNotificationSetting<T extends keyof NotificationSettings>(
    setting: T,
    value: NotificationSettings[T],
  ): Promise<void> {
    if (this._notificationSettings === undefined) {
      return
    }

    this._notificationSettings[setting] = value
    this._notificationSettings = await backend.putNotificationSettings(
      this._notificationSettings,
    )
  }
}

/**
 * Check if the user has at least one app-level notification enabled.
 */
export function isAnyNotificationPreferenceSet(
  notificationSettings: NotificationSettings,
): boolean {
  return Object.values(notificationSettings).some((value) => value === true)
}

// View for ChangePasswordPopup.vue
export class ChangePasswordPopupView {
  public validation: ValidationState = new ValidationState()
  public state: string = 'form'

  public model: NewPassword = { old_password: '', password: '' }

  async init(): Promise<void> {
    this.model.old_password = ''
    this.model.password = ''
    this.state = 'form'
  }

  async submitForm(): Promise<void> {
    this.validation.reset()
    try {
      await backend.changePassword(this.model)
      this.state = 'success'
    } catch (error: any) {
      this.state = 'form'
      this.validation.showErrors(error)
    }
  }
}

// View for ChangeEmailPopup.vue
export class ChangeEmailPopupView {
  public validation: ValidationState = new ValidationState()
  public state: string = 'form'

  public model: NewEmail = { email: '' }

  async initWithEmail(email: string): Promise<void> {
    this.model.email = email
    this.state = 'form'
  }

  async submitForm(): Promise<void> {
    this.validation.reset()
    if (this.model.email === getEmail()) {
      this.state = 'form'
      this.validation.showMessage('email', 'Address has not changed')
      return
    }
    try {
      await backend.changeEmail(this.model)
      this.state = 'success'
    } catch (error: any) {
      this.state = 'form'
      this.validation.showErrors(error)
    }
  }
}
